import { ActionConfirmation } from 'Components/campaigns/applications/ApplicationCard'
import CloseButton from 'Components/Common/CloseButton'
import InstagramCaption from 'Components/Common/InstagramCaption'
import { PRIVILEGE } from 'constants/privilege'
import { usePrivileges } from 'hooks/useAuth'
import React, { Ref, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import { Modal, Spinner } from 'reactstrap'
import { processCollaboration as processCollaborationAPI, processCollaborationMilestoneMedia } from 'services/api/campaign/collaboration'
import { updatePostStatus } from 'slices/campaigns/collaborations/reducer'
import { fetchPost, fetchSocialPostThunk } from 'slices/influencer/thunk'
import { closeSocialPostModal } from 'slices/ui/reducer'
import { AppDispatch, RootState, store } from 'store'
import { Influencer, SocialAccount } from 'types/influencer'
import { formatNumber, getErrorMessage } from 'utils/helpers'
import PerfectScrollbar from 'react-perfect-scrollbar'
import userProfilePicture from "../../assets/images/users/user-dummy-img.jpg"
import SocialPostComments from './comment/SocialPostComments'
import { Comment } from 'types/social-account'
import AutoGrowTextarea from 'Components/input/AutoGrowTextarea'
import { deleteSocialPostComment, fetchSocialPostComments, replyPrivatelySocialPostComment, replySocialPostComment } from 'services/api/social-account'
import Swal from 'sweetalert2'

interface SocialPostModalProps {
    isOpen: boolean,
    onClose: () => void,
    postId?: string,
    socialAccount?: SocialAccount
}

export const trashIcon = `
<svg width="80" height="80" viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg">
  <circle cx="40" cy="40" r="40" fill="#F1F5F4"/>

  <path
    d="M30 26H50"
    stroke="#0069AD"
    stroke-width="2"
    stroke-linecap="round"
  />

  <path
    d="M34 26V23.5C34 22.67 34.67 22 35.5 22H44.5C45.33 22 46 22.67 46 23.5V26"
    stroke="#0069AD"
    stroke-width="2"
    stroke-linecap="round"
  />

  <path
    d="M28 26L30 54C30.06 54.83 30.75 55.5 31.58 55.5H48.42C49.25 55.5 49.94 54.83 50 54L52 26"
    fill="#BBD8ED"
    stroke="#0069AD"
    stroke-width="2"
    stroke-linejoin="round"
  />

  <line x1="36" y1="32" x2="36" y2="50" stroke="#00A7E4" stroke-width="2" stroke-linecap="round"/>
  <line x1="44" y1="32" x2="44" y2="50" stroke="#00A7E4" stroke-width="2" stroke-linecap="round"/>
</svg>
`

const SocialPostModal: React.FC<SocialPostModalProps> = ({ isOpen, onClose, postId, socialAccount }) => {

    const dispatch: AppDispatch = useDispatch();

    const privileges = usePrivileges();

    const selectedAgencyId = useSelector((state: RootState) => state.auth.selectedAgencyId);

    const post = useSelector((state: RootState) => state.influencers.social_posts[socialAccount?.uuid!]?.[postId!]?.post);
    const postLoading = useSelector((state: RootState) => state.influencers.postLoading);
    const [replyingTo, setReplyingTo] = useState<Comment | null>(null);
    const [privatelyReplyingTo, setPrivatelyReplyinTo] = useState<Comment | null>(null);
    const [reply, setReply] = useState("");

    const [replyTextareaRef, setReplyTextAreaRef] = useState<any>(null)


    const [posting, setPosting] = useState(false);

    const [comments, setComments] = useState<Comment[]>([]);
    const [loading, setLoading] = useState(false);


    useEffect(() => {
        if (socialAccount && postId && selectedAgencyId) {
            setLoading(true);
            fetchSocialPostComments(selectedAgencyId, socialAccount.uuid, postId).then((items: Comment[]) => {
                setComments(items);
            }).finally(() => {
                setLoading(false);
            })
        }
    }, [socialAccount, postId, selectedAgencyId])



    useEffect(() => {
        if (postId && socialAccount) {
            dispatch(fetchSocialPostThunk({ socialAccountId: socialAccount.uuid, postId }));
        }
    }, [dispatch, postId, socialAccount])

    const onModalClose = (processed = false) => {
        dispatch(closeSocialPostModal());
        onClose();
    }

    const onReply = (comment: Comment) => {
        setReplyingTo(comment);
    }

    const onReplyPrivately = (comment: Comment) => {
        setPrivatelyReplyinTo(comment)
    }

     const popReply = (commentId: string) => {
        let _comments = [...comments];
        if(_comments.map((c) => c.id).includes(commentId)){
            setComments([..._comments].filter((c) => c.id !== commentId));
            return;
        }
        for(let i=0; i<_comments.length; i++){
            const repliesData = _comments[i]?.replies?.data;
            let replies = Array.isArray(repliesData) ? [...repliesData] : [];
            if(replies.map((r) => r.id).includes(commentId)){
                replies = replies.filter((r) => r.id !== commentId);
                _comments[i].replies = {
                    ..._comments[i].replies,
                    data: replies
                };
                setComments(_comments);
                return;
            }
        }
    }

    const onDelete = async (comment: Comment, reset?: () => void) => {
        const result = await Swal.fire({
            title: "Are you sure you want to delete this comment/reply?",
            iconHtml: trashIcon,
            showCancelButton: true,
            confirmButtonText: "Delete",
            cancelButtonText: "Cancel",
            reverseButtons: true
        });

        if (result.isConfirmed && selectedAgencyId && socialAccount && postId && comment) {
            try {
                await deleteSocialPostComment(selectedAgencyId, socialAccount.uuid, postId, comment.id)
                popReply(comment.id);
            } catch (error: any) {
                toast.error(error.message || "Failed to delete comment/reply.")
            } finally {
                reset?.();
            }
        }else {
            reset?.();
        }
    }

    useEffect(() => {
        if (replyingTo) {
            setPrivatelyReplyinTo(null);
            setTimeout(() => {
                setReply(`@${replyingTo?.from?.username} `);
                if (replyTextareaRef?.current) {
                    replyTextareaRef.current.focus?.();
                }
            }, 10)
        } else {
            setReply('');
        }
    }, [replyingTo, replyTextareaRef])

    useEffect(() => {
        if (privatelyReplyingTo) {
            setReplyingTo(null);
            setTimeout(() => {
                setReply(`Hi @${privatelyReplyingTo.from.username}, `);
                if (replyTextareaRef?.current) {
                    replyTextareaRef.current.focus?.();
                }
            }, 10)
        } else {
            setReply('');
        }
    }, [privatelyReplyingTo, replyTextareaRef])

    useEffect(() => {
        setReply('');
        setReplyingTo(null);
    }, [postId, socialAccount])

    const pushReply = (commentId: string, replyId: string, replyText: string) => {
        let _comments = [...comments];
        for(let i=0; i<_comments.length; i++){
            const repliesData = _comments[i]?.replies?.data;
            let replies = Array.isArray(repliesData) ? [...repliesData] : [];
            if(_comments[i].id === commentId || replies.map((r) => r.id).includes(commentId)){
                replies.push({
                    id: replyId,
                    text: replyText,
                    like_count: 0,
                    hidden: false,
                    timestamp: new Date().toISOString(),
                    replies: {
                        data: []
                    },
                    from: {
                        username: socialAccount?.handle ?? '',
                        id: socialAccount?.uuid ?? ''
                    }
                })
                _comments[i].replies = {
                    ..._comments[i].replies,
                    data: replies
                };
                setComments(_comments);
                return;
            }
        }
    }

    const sendReply = async (message: string, comment: Comment) => {
        if (!selectedAgencyId || !socialAccount?.uuid || !postId || !message || !comment?.id) return;
        setPosting(true);
        try {
            const response = await replySocialPostComment(selectedAgencyId, socialAccount.uuid, postId, comment.id, message);
            if(response?.comment?.id){
                pushReply(comment.id, response.comment.id, message);
            }
        } catch (error) {
            console.log(error);
            toast.error(getErrorMessage(error, "Failed to reply!"));
        } finally {
            setPosting(false);
            setReply('');
            setReplyingTo(null);
        }
    }

    const sendPrivateReply = async (message: string, comment: Comment) => {
        if (!selectedAgencyId || !socialAccount?.uuid || !postId || !message || !comment?.id) return;
        setPosting(true);
        try {
            const response = await replyPrivatelySocialPostComment(selectedAgencyId, socialAccount.uuid, postId, comment.id, message);
            toast.success("Private reply sent to their DM!");
        } catch (error) {
            console.log(error);
            toast.error(getErrorMessage(error, "Failed to reply!"));
        } finally {
            setPosting(false);
            setReply('');
            setPrivatelyReplyinTo(null);
        }
    }

    const onSend = () => {
        const message = reply.trim();
        if (!message) return;
        if (replyingTo) {
            sendReply(message, replyingTo)
        } else if (privatelyReplyingTo) {
            sendPrivateReply(message, privatelyReplyingTo)
        }
    }

    return (
        <Modal isOpen={isOpen} toggle={() => onModalClose()} size='lg' className='post-modal'>
            <CloseButton top='0' onClose={onModalClose} />
            {
                postId && socialAccount && post &&
                <div className="d-flex flex-column flex-lg-row gap-2 gap-lg-0">
                    {
                        post.platform === 'ig' && post.ig &&
                        <>
                            {
                                post?.ig?.media_type === 'VIDEO' &&
                                <video src={post?.ig?.media_url} controls autoPlay className='post' controlsList='nodownload nofullscreen noremoteplayback' />
                            }
                            {
                                (post?.ig?.media_type === 'IMAGE' || post?.ig?.media_type === 'CAROUSEL_ALBUM') &&
                                <img src={post?.ig?.media_url} className='post' />
                            }
                        </>
                    }
                    {
                        post.platform === 'yt' && post.yt &&
                        <>
                            {
                                post?.type === 'VIDEO' &&
                                <iframe
                                    src={post.yt.permalink ? `https://www.youtube.com/embed/${post.yt.permalink.split('/').pop()?.split('?')[0]}` : ""}
                                    className="mw-100 mh-800px align-self-center"
                                    style={{ objectFit: "contain", width: '100%', height: '740px', maxHeight: '75vh' }}
                                    frameBorder="0"
                                    allowFullScreen
                                    title="YouTube video"
                                />
                            }
                        </>
                    }


                    <div className="post-details">
                        <div className="d-flex flex-column gap-3 ">
                            <div className="d-flex gap-2 justify-content-between align-items-center">
                                <div className="post-profile d-flex gap-3 p-4 pb-0">
                                    <img src={socialAccount?.profile_picture_url ?? userProfilePicture} className="post-influencer-pic" />
                                    <div className="name-and-date d-flex flex-column align-items-start ">
                                        <span className="influencer-name">
                                            {
                                                socialAccount?.platform === 'ig' && socialAccount.ig?.name
                                            }
                                            {
                                                socialAccount?.platform === 'yt' && (socialAccount.yt?.title ?? socialAccount?.yt?.username)
                                            }
                                        </span>
                                        <span className='influencer-address'>{post?.posted_on}</span>
                                    </div>
                                </div>
                                <a title='View on Instagram' className='p-4 pb-0' href={post.ig?.permalink} target='_blank'><span className='mdi mdi-open-in-new fs-4'></span></a>
                            </div>

                            <PerfectScrollbar style={{ height: '600px' }}>
                                <div className='d-flex flex-column gap-3 p-4 pt-0'>
                                    <InstagramCaption text={post?.platform === 'ig' ? (post?.ig?.caption ?? '') : post?.platform === 'yt' ? (post?.yt?.title ?? '') : ''} />
                                    <div className="divider mt-n2"></div>
                                    <div className='d-flex align-items-center justify-content-start gap-4 '>
                                         <div className='d-flex align-items-center gap-1' title='Likes'>
                                            <span className="mdi mdi-heart-outline fs-4"></span>
                                            {formatNumber(post.ig?.like_count ?? 0)}
                                        </div>
                                        <div className='d-flex align-items-center gap-1' title='Comments'>
                                            <span className="mdi mdi-comment-outline fs-4"></span>
                                            {formatNumber(post.ig?.comments_count ?? 0)}
                                        </div>
                                        <div className='d-flex align-items-center gap-1' title='Views'>
                                            <span className="mdi mdi-eye-outline fs-4"></span>
                                            {formatNumber(post.ig?.views_count ?? 0)}
                                        </div>
                                        <div className='d-flex align-items-center gap-1' title='Shares'>
                                            <span className="mdi mdi-share-outline fs-4"></span>
                                            {formatNumber(post.ig?.shares_count ?? 0)}
                                        </div>
                                        <div className='d-flex align-items-center gap-1' title='Reach'>
                                            <span className="mdi mdi-chart-bar fs-4"></span>
                                            {formatNumber(post.ig?.reach_count ?? 0)}
                                        </div>
                                    </div>
                                    <div className="divider"></div>
                                    <SocialPostComments onReply={onReply} onDelete={onDelete} onReplyPrivately={onReplyPrivately} comments={comments} loading={loading} />
                                </div>
                            </PerfectScrollbar>
                            <div className='comment-reply-input-wrapper flex-column'>
                                {
                                    replyingTo &&
                                    <div className='replying-to'>Replying to {replyingTo.from?.username} <span onClick={() => setReplyingTo(null)} className='p-1 cursor-pointer'><i className='mdi mdi-close'></i></span></div>
                                }
                                {
                                    privatelyReplyingTo &&
                                    <div className='replying-to'>Privately Replying to {privatelyReplyingTo.from?.username} <span onClick={() => setPrivatelyReplyinTo(null)} className='p-1 cursor-pointer'><i className='mdi mdi-close'></i></span></div>
                                }
                                <div className="d-flex gap-2 align-items-center">
                                    <AutoGrowTextarea disabled={((!replyingTo && !privatelyReplyingTo) || posting)} onReady={(textareaRef) => setReplyTextAreaRef(textareaRef)} value={reply} onChange={(v) => setReply(v)} placeholder='Select a comment to reply...' />
                                    <button onClick={onSend} disabled={posting} className='reply-btn'>{posting ? 'Posting...' : 'Post'}</button>
                                </div>

                            </div>
                        </div>

                    </div>

                </div>
            }
            {
                (!postId || !socialAccount) &&
                <span>No media found!</span>
            }

        </Modal>
    )
}

export default SocialPostModal